import { Node } from "../Core/Node";
import { DomWrapper } from "../Wrapper/DomWrapper";
import type { IDomWrapable } from "../../interfaces/IDomWrapable";

import type {
    IBackgroundStyle,
    IBorderStyle,
    IColorStyle,
    IFontStyle,
} from "../Styles";

const DEFAULT_WIDTH = 100;
const DEFAULT_HEIGHT = 100;

export type ElementOptions = {
    id?: string;
    dom?: HTMLElement;
    width?: number;
    height?: number;
};

export enum ElementEvent {
    DISPOSE = "dispose",
}

/**
 * Element 类是 DOM 元素的封装类，继承自 Node
 * 提供对 HTML 元素的操作、样式调整和 DOM 树操作等功能
 * 作为 2D 可视化系统中处理实际 DOM 元素渲染的基础类
 */
export class Element extends Node {
    // 标记元素是否已经添加到 DOM 中
    private isAppended: boolean = false;

    // 元素宽度(不会因元素的内容而改变，原则上内容宽度不可超出本值)
    private _width: number = 0;
    // 元素高度(不会因元素的内容而改变，原则上内容高度不可超出本值)
    private _height: number = 0;
    /** 目标包装器，负责实际操作 DOM 元素 */
    override target: IDomWrapable;

    /** 获取元素宽度 */
    get width(): number { return this._width; }
    /** 获取元素高度 */
    get height(): number { return this._height; }

    /**
     * 创建一个元素实例
     * @param options 配置对象，可指定 DOM 元素、宽度、高度和 ID
     */
    constructor(options?: ElementOptions) {

        const { id, dom } = options || {};

        let initialId: string | undefined = undefined;
        let elementDom: HTMLElement;

        // 确定 ID：优先使用 options.id，其次使用 dom.id
        if (id) {
            initialId = id;
        } else if (dom?.id) {
            initialId = dom.id;
        }

        super({ id: initialId }); // 调用 Node 构造函数，传递 id

        // 判断是否提供了 DOM 元素
        if (dom) {
            // 使用现有的 DOM 元素
            elementDom = dom;
            this.target = new DomWrapper(this, elementDom);
            this._width = elementDom.offsetWidth;
            this._height = elementDom.offsetHeight;
        } else {
            // 创建新的 div 元素
            elementDom = document.createElement('div');
            this.target = new DomWrapper(this, elementDom);

            // 使用配置对象或默认值设置尺寸
            const {
                width = DEFAULT_WIDTH,
                height = DEFAULT_HEIGHT
            } = options || {};

            this.setWidth(width);
            this.setHeight(height);
        }
    }

    /**
     * 更新目标元素的变换（位置、旋转、缩放等）
     */
    protected updateTargetTransform(): void {
        // const { x, y } = this.globalPosition;
        // const rotation = this.globalRotation;
        // const { x: scaleX, y: scaleY } = this.globalScaling;

        const { x, y } = this.position;
        const rotation = this.rotation;
        const { x: scaleX, y: scaleY } = this.scaling;

        this.target?.moveTo(x, y);
        this.target?.rotateTo(rotation);
        this.target?.scaleTo(scaleX, scaleY);
    }

    /**
     * 在元素附加到 DOM 前更新定位样式
     */
    protected updateDomPositioningStyle(): void {
        if (this.target && this.target instanceof DomWrapper) {
            this.target.updatePositioningStyle();
        }
    }

    /**
     * 实现渲染，将元素附加到 DOM 并更新变换
     */
    override render(): void {
        this.attachToDOM();
        this.updateTargetTransform();
    }

    /**
     * 设置元素宽度
     * @param value 要设置的宽度值
     */
    override setWidth(value: number) {
        this.setSize({ width: value });
    }

    /**
     * 设置元素高度
     * @param value 要设置的高度值
     */
    override setHeight(value: number) {
        this.setSize({ height: value });
    }

    /**
     * 设置元素尺寸
     * @param size 包含宽度和/或高度的对象
     */
    override setSize(size: { width?: number, height?: number }): void {
        const { width, height } = size;
        if (width !== undefined) {
            this._width = width;
            this.target.setWidth(width);
        }
        if (height !== undefined) {
            this._height = height;
            this.target.setHeight(height);
        }
    }

    /**
     * 更新元素的层级，更新 DOM 中的 z-index
     */
    override updateLevel() {
        super.updateLevel();
        this.target.setLevel(this.level);
    }

    /**
     * 设置元素内容
     * @param html 可选，HTML 字符串、HTML 元素或 HTML 元素数组
     * @returns 未提供参数时返回元素的 innerHTML
     */
    setContent(html: string | HTMLElement | HTMLElement[]): void {
        this.target.htmlEle.innerHTML = ''; // 清空现有内容
        if (typeof html === 'string') {
            this.target.htmlEle.innerHTML = html;
        } else if (html instanceof HTMLElement) {
            this.target.htmlEle.appendChild(html);
        } else if (Array.isArray(html)) {
            html.forEach(el => {
                if (el instanceof HTMLElement) {
                    this.target.htmlEle.appendChild(el);
                } else {
                    console.warn('Invalid element in HTMLElement array for setContent');
                }
            });
        } else {
            throw new Error('Invalid content type. Must be string, HTMLElement, or HTMLElement[].');
        }
    }

    /**
     * 释放元素资源
     * 移除事件监听器并从 DOM 中分离元素
     */
    dispose(): void {
        // 从父 DOM 元素中分离
        this.detachFromDOM();

        // 触发 dispose 事件
        this.emit(ElementEvent.DISPOSE, this);

        // 移除所有事件监听器
        this.removeAllListeners();
    }

    /**
     * 将元素附加到父 DOM 元素
     * @returns 如果操作导致 DOM 变化则返回 true
     */
    attachToDOM(): boolean {
        // 先更新定位样式，再附加到 DOM
        this.updateDomPositioningStyle();

        if (!this.isAppended && this.parent) {
            const dom = this.target.htmlEle;
            const parentDom = this.parent.target.htmlEle;

            if (!parentDom.contains(dom)) {
                parentDom.appendChild(dom);
                this.isAppended = true;
                return true;
            }
        }
        return false;
    }

    /**
     * 从父 DOM 元素中分离
     * @returns 如果操作导致 DOM 变化则返回 true
     */
    detachFromDOM(): boolean {
        if (this.isAppended && this.target.htmlEle.parentElement) {
            this.target.htmlEle.parentElement.removeChild(this.target.htmlEle);
            this.isAppended = false;
            return true;
        }
        return false;
    }

    /**
     * 应用字体样式到元素
     * @param fontStyle 字体样式对象
     */
    override applyFont(fontStyle: IFontStyle): void {
        super.applyFont(fontStyle);
        this.target.applyFont(fontStyle);
    }

    /**
     * 应用颜色样式到元素
     * @param colorStyle 颜色样式对象
     */
    override applyColor(colorStyle: IColorStyle): void {
        super.applyColor(colorStyle);
        this.target.applyColor(colorStyle);
    }

    /**
     * 应用边框样式到元素
     * @param borderStyle 边框样式对象
     */
    override applyBorder(borderStyle: IBorderStyle): void {
        super.applyBorder(borderStyle);
        this.target.applyBorder(borderStyle);
    }

    /**
     * 应用背景样式到元素
     * @param backgroundColor 背景样式对象
     */
    override applyBackground(backgroundColor: IBackgroundStyle): void {
        super.applyBackground(backgroundColor);
        this.target.applyBackground(backgroundColor);
    }
}